import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  FormControl,
  FormLabel,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import { formatInputPhoneNumber } from '../../../helpers';
import { ImageUpload } from '../../../components';
import { IDropdownResponse } from '../../../models';
import { getPeopleAgentImage } from '../../../fetch';
import { getAdminLookupNeighborhoods } from '../../../fetch/lookups';

export const AgentFields = ({
  setFieldValue,
  values,
  isFormDisabled,
  touched,
  errors,
  handleBlur,
  currentExternalPersonId,
  showContactInfo = true
}) => {
  const [neighborhoodsValues, setNeighborhoodsValues] = useState<IDropdownResponse[]>([]);
  const [inputValue, setInputValue] = useState('');
  const { isLoading: isLoadingNeighborhoods, data: neighborhoodsData } = useQuery<
    IDropdownResponse[],
    Error
  >(['agent-neighborhoods'], () => getAdminLookupNeighborhoods(), {
    onSuccess: d => {
      const options = d.filter(record => {
        return values.agentNeighborhoodIds.includes(Number(record?.value));
      });
      setNeighborhoodsValues(options);
    }
  });
  const { isLoading: isLoadingPersonalImage, data: personalImage } = useQuery<any, Error>(
    ['agent-personal-image', currentExternalPersonId],
    () => getPeopleAgentImage(currentExternalPersonId, 'personal'),
    {
      enabled: !!currentExternalPersonId
    }
  );
  const { isLoading: isLoadingLogoImage, data: logoImage } = useQuery<any, Error>(
    ['agent-logo-image', currentExternalPersonId],
    () => getPeopleAgentImage(currentExternalPersonId, 'logo'),
    {
      enabled: !!currentExternalPersonId
    }
  );
  const neighborhoods = useMemo(() => neighborhoodsData ?? [], [neighborhoodsData]);

  return (
    <>
      {showContactInfo && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              variant="outlined"
              autoComplete="nope"
              label="First Name"
              name="firstName"
              value={values.firstName}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              size="small"
              inputProps={{
                'data-lpignore': 'true'
              }}
              error={touched.firstName && errors && errors.firstName ? true : false}
              helperText={touched.firstName && errors && errors.firstName}
              onChange={e => setFieldValue('firstName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              variant="outlined"
              autoComplete="nope"
              label="Last Name"
              name="lastName"
              inputProps={{
                'data-lpignore': 'true'
              }}
              value={values.lastName}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              size="small"
              error={touched.lastName && errors && errors.lastName ? true : false}
              helperText={touched.lastName && errors && errors.lastName}
              onChange={e => setFieldValue('lastName', e.target.value)}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          variant="outlined"
          autoComplete="nope"
          label="Agency"
          name="workplace"
          required
          error={touched.workplace && errors && errors.workplace ? true : false}
          helperText={touched.workplace && errors && errors.workplace}
          value={values.workplace}
          disabled={isFormDisabled}
          onBlur={handleBlur}
          size="small"
          onChange={e => {
            setFieldValue('workplace', e.target.value);
          }}
        />
      </Grid>
      {showContactInfo && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth={true}
              id="email"
              name="email"
              label="Email (Username)"
              size="small"
              required
              error={touched.email && errors && errors.email ? true : false}
              helperText={touched.email && errors && errors.email}
              autoComplete="off"
              value={values.email}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              inputProps={{
                'data-lpignore': true // Last Pass disable auto-fill
              }}
              onChange={e => setFieldValue('email', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              variant="outlined"
              autoComplete="nope"
              label="Phone Number"
              name="phone"
              value={values.phoneNumber}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              size="small"
              onChange={e => {
                setFieldValue('phoneNumber', formatInputPhoneNumber(e.target.value));
              }}
              error={Boolean(touched.phoneNumber && errors.phoneNumber)}
              helperText={touched.phoneNumber && errors.phoneNumber}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="nope"
              label="Cell Phone Number"
              name="phone"
              value={values.cellPhoneNumber}
              disabled={isFormDisabled}
              onBlur={handleBlur}
              size="small"
              onChange={e => {
                setFieldValue('cellPhoneNumber', formatInputPhoneNumber(e.target.value));
              }}
              error={Boolean(touched.cellPhoneNumber && errors.cellPhoneNumber)}
              helperText={touched.cellPhoneNumber && errors.cellPhoneNumber}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <TextField
          fullWidth
          variant="outlined"
          autoComplete="nope"
          label="Website"
          name="webSite"
          value={values.webSite}
          disabled={isFormDisabled}
          onBlur={handleBlur}
          size="small"
          error={touched.webSite && errors && errors.webSite ? true : false}
          helperText={touched.webSite && errors && errors.webSite}
          onChange={e => {
            setFieldValue('webSite', e.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          id="agent-neighborhoods"
          value={neighborhoodsValues}
          onChange={(event, newValue: any[]) => {
            setNeighborhoodsValues(newValue);
            setFieldValue(
              'agentNeighborhoodIds',
              newValue.map(val => Number(val.value))
            );
          }}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          options={neighborhoods}
          getOptionLabel={option => option.description}
          filterSelectedOptions
          renderOption={(props, option: IDropdownResponse) => {
            return (
              <li {...props} key={`${option.value}`}>
                {option.description}
              </li>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              disabled={isLoadingNeighborhoods}
              label={isLoadingNeighborhoods ? 'Loading...' : 'Neighborhoods'}
              placeholder="Add Agent Neighborhood"
            />
          )}
        />
      </Grid>
      {showContactInfo && (
        <>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              color="primary"
              control={
                <Checkbox
                  checked={values.isSalesAdmin}
                  id="isSalesAdmin"
                  onChange={(_, checked) => setFieldValue('isSalesAdmin', checked)}
                />
              }
              label="Agent is Sales Admin"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              color="primary"
              control={
                <Checkbox
                  checked={values.isSalesCoordinator}
                  id="isSalesCoordinator"
                  onChange={(_, checked) => setFieldValue('isSalesCoordinator', checked)}
                />
              }
              label="Agent is Sales Coordinator"
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          color="primary"
          control={
            <Checkbox
              checked={values.includeAgentNameInUrl}
              id="includeAgentNameInUrl"
              onChange={(_, checked) => setFieldValue('includeAgentNameInUrl', checked)}
            />
          }
          label="Include Agent Name in URL"
        />
      </Grid>
      <Grid item xs={12}>
        <Typography paragraph>
          NOTE: This should be the root address. Example:
          http://www.[yourWebAddress].com/Neighborhoods/
        </Typography>
        <Typography paragraph>
          We add to the address the neighborhood name. Example:
          http://www.[yourWebAddress].com/Neighborhoods/Lundy Chase 3
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <FormLabel>Personal Photo</FormLabel>
          <ImageUpload
            showLabel={false}
            imageKey={`personal`}
            disabled={isLoadingPersonalImage}
            handleFileChange={(val, file) => {
              setFieldValue('uploadPersonalFile', file);
            }}
            image={
              currentExternalPersonId && !!personalImage
                ? `${window.location.origin}/api/people/agents/${currentExternalPersonId}/photos/personal`
                : null
            }
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <FormLabel>Company Logo Image</FormLabel>
          <ImageUpload
            imageKey={`logo`}
            showLabel={false}
            disabled={isLoadingLogoImage}
            handleFileChange={(val, file) => {
              setFieldValue('uploadLogoFile', file);
            }}
            image={
              currentExternalPersonId && !!logoImage
                ? `${window.location.origin}/api/people/agents/${currentExternalPersonId}/photos/logo`
                : null
            }
          />
        </FormControl>
      </Grid>
    </>
  );
};
